/** @jsxImportSource @emotion/react */
import React, {useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {createSelector} from "reselect";
import {useTranslation} from "react-i18next";

import {useAuth} from "../auth/auth";
import {Tag} from "@blueprintjs/core";

// Libs
import Button from "../common/components/button";
import {boxShadow, primaryColor, activeColor, orangeColor} from "../common/libs/theme-lib";

// Components
import VirtualizedTable from "../common/components/virtualized-table";
import moment from "moment";

// Actions 
import { readExecutions, createExecutions } from "./executions-actions";

// Selectors 
import { executionsSelector, executionsIsFetchingSelector, executionsErrorSelector } from "./executions-selectors";

// Component Selector
const executionsPageSelector = createSelector(
    executionsSelector,
    executionsIsFetchingSelector,
    executionsErrorSelector,
    (executions, executionsIsFetching, executionsError) => {
        executions = executions.toJS();
        executions = (Array.isArray(executions) && executions) || [];
        return {
            executions: executions,
            executionsIsFetching,
            executionsError,
        }
    }
);

// Redux dispatch hook
const useReduxDispatch = () => {
    const dispatch = useDispatch();
    return {
        readExecutions: () => dispatch(readExecutions()),
        createExecutions: (userId, type) => dispatch(createExecutions(userId, type))
    }
};

const ExecutionsPage = (props) => {

    // Redux
    const {
        executions, executionsIsFetching, executionsError,
    } = useSelector(state => executionsPageSelector(state));
    const {readExecutions, createExecutions} = useReduxDispatch();

    const auth = useAuth();
    const userId = auth.userId;

    // This use effect executed one time
    useEffect(() => {
        readExecutions();
        const interval = setInterval(() => readExecutions(), 15000);
        return () => clearInterval(interval)
    }, []);

    //i18n
    const {t} = useTranslation("executions");

    // Button event handler to get list of executions from server
    function getData() {

        // Check if there is already a running execution, to avoid concurrent executions
        const numberOfRunningExecutions = executions.filter(execution => execution.status === 'running')
        if (numberOfRunningExecutions.length === 0) {
            const type = "manual";
            createExecutions(userId,type);
            readExecutions();
        } else {
            alert(t("runningExecution"));
        }   
    }

    return (
        <>
            <div style={{
                display: "flex",
                alignItems: "flex-end",
                paddingBottom: "15px"
            }}>
                <div style={{flex: 1}}>
                    <div style={{
                        color: primaryColor,
                        fontSize: 28,
                        fontWeight: "bold",
                        marginBottom: 10
                    }}>{t("title")}</div>
                </div>
            </div>
            <div style={{
                display: "flex",
                flex: 1,
            }}>
                <div style={{
                    backgroundColor: "white",
                    boxShadow: boxShadow,
                    padding: 15,
                    display: "flex",
                    flexDirection: "column",
                    height: "min-content",
                    marginRight: 15
                }}>
                    <p style={{marginBottom: 0}}><strong>{t("description")}</strong></p>
                    <div style={{
                        margin: "30px auto",
                        display: "flex",
                        justifyContent: "space-evenly",
                        width: "calc(100% - 30px)"
                    }}>
                    </div>
                    <Button
                        text={t("button")}
                        action={() => getData()}
                        icon="download"
                        intent="primary"
                        style={{
                            maxWidth: 250,
                            margin: "auto"
                        }}
                    />
                </div>
                <div style={{
                        backgroundColor: "white",
                        boxShadow: boxShadow,
                        padding: 15,
                        flex: 1,
                        display: "flex",
                        flexDirection: "column"
                    }}>

                    <VirtualizedTable
                        config={{
                            sortable: false,
                            showFooter: true,
                            key: "_id"
                        }}
                        columns={[{
                            id:"StartTime",
                            Header: t("executionsTableColumns.startTime"),
                            accessor: row => moment(row.start_timestamp).format("DD/MM/YYYY LTS"),
                            style: {
                                display: "flex",
                                alignItems: "center"
                            },
                        },
                        {
                            id:"FinishTime",
                            Header: t("executionsTableColumns.finishTime"),
                            accessor: row =>(row.finish_timestamp ? moment(row?.finish_timestamp).format("DD/MM/YYYY LTS") : "---"),
                            style: {
                                display: "flex",
                                alignItems: "center"
                            },
                        },
                        {
                            id:"Duration",
                            Header: t("executionsTableColumns.duration"),
                            accessor: row => (row.finish_timestamp ? moment(moment(row?.finish_timestamp).diff(moment(row?.start_timestamp))).format("mm:ss") : "---"),
                            style: {
                                display: "flex",
                                alignItems: "center"
                            },
                        },
                        {
                            id:"Type",
                            Header: t("executionsTableColumns.type"),
                            accessor: row => (row.type === "manual" ? t("manual") : t("automatic")),
                            style: {
                                display: "flex",
                                alignItems: "center"
                            },
                        },
                        {
                            id: "Status",
                            Header: t("executionsTableColumns.status"),
                            accessor: row => (
                                row.status === "success" ? 
                                    <Tag
                                        style={{
                                            ...tagStyle,
                                            backgroundColor: activeColor
                                        }}
                                        large={true}
                                        intent={"success"}
                                    >
                                        {t("success")}
                                    </Tag> : 
                                row.status === "error" ? 
                                    <Tag
                                        style={tagStyle}
                                        large={true}
                                        intent={"danger"}
                                    >
                                        {t("error")}
                                    </Tag> : 
                                    <Tag
                                        style={{
                                            ...tagStyle,
                                            backgroundColor: orangeColor
                                        }}
                                        large={true}
                                    >
                                        {t("running")}
                                    </Tag>
                                ),
                            style: {
                                display: "flex",
                                alignItems: "center"
                            },
                        },
                        {
                            id:"User",
                            Header: t("executionsTableColumns.user"),
                            accessor: row => row.user?.full_name,    
                            style: {
                                display: "flex",
                                alignItems: "center"
                            },
                        }]}
                        data={executions}
                    />
                </div>
            </div>
        </>
    );
};

export default ExecutionsPage;

const tagStyle = {
    minWidth: "100px",
    textAlign: "center"
};